/**
 * Matomo Analytics — SDK init & tracking helpers
 *
 * Production-only: initializes only when VITE_MATOMO_URL and VITE_MATOMO_SITE_ID are set.
 * All exported functions are silent no-ops if Matomo is not initialized.
 *
 * Mirrors scio1's Matomo setup (matomo.optisigns.com, Site ID 1, *.optisigns.com cookie domain).
 * Custom dimensions match scio1 analytics-configuration.js:
 *   Dim 1: userId, Dim 2: email, Dim 5: accountId
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';

declare global {
  interface Window {
    _paq: unknown[][];
  }
}

const MATOMO_URL = import.meta.env.VITE_MATOMO_URL as string | undefined;
const MATOMO_SITE_ID = import.meta.env.VITE_MATOMO_SITE_ID as string | undefined;

let initialized = false;

export function initMatomo(): void {
  if (!MATOMO_URL || !MATOMO_SITE_ID) return;

  window._paq = window._paq || [];
  const _paq = window._paq;

  _paq.push(['setCookieDomain', '*.optisigns.com']);
  _paq.push(['setExcludedQueryParams', ['/verify']]);
  _paq.push(['trackPageView']);
  _paq.push(['enableLinkTracking']);
  _paq.push(['setTrackerUrl', MATOMO_URL + 'matomo.php']);
  _paq.push(['setSiteId', MATOMO_SITE_ID]);

  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://download.optisignsapp.com/js/matomo.5.0.2.js';
  const firstScript = document.getElementsByTagName('script')[0];
  firstScript?.parentNode?.insertBefore(script, firstScript);

  initialized = true;
}

/**
 * Set user identity in Matomo custom dimensions.
 * Called after user loads (equivalent to scio1 analytics-configuration.js startup).
 */
export function matomoIdentify(userId: string, email: string, accountId: string): void {
  if (!initialized) return;
  window._paq.push(['setCustomDimension', 1, userId]);
  window._paq.push(['setCustomDimension', 2, email]);
  window._paq.push(['setCustomDimension', 5, accountId]);
}

export function matomoTrackPageView(url: string): void {
  if (!initialized) return;
  window._paq.push(['setCustomUrl', url]);
  window._paq.push(['setDocumentTitle', url]);
  window._paq.push(['trackPageView']);
}

export function matomoTrackEvent(category: string, action: string, name?: string): void {
  if (!initialized) return;
  if (name !== undefined) {
    window._paq.push(['trackEvent', category, action, name]);
  } else {
    window._paq.push(['trackEvent', category, action]);
  }
}

export function matomoReset(): void {
  if (!initialized) return;
  window._paq.push(['resetUserId']);
  window._paq.push(['deleteCookies']);
}

/**
 * React hook: fires matomoTrackPageView on every route change.
 * Skips the initial mount — initMatomo() already fires the first trackPageView.
 * Equivalent to scio1's $stateChangeStart listener in signageCloud.js.
 */
export function useMatomoPageTracking(): void {
  const location = useLocation();
  const isMountedRef = useRef(false);

  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      return; // skip initial mount — initMatomo() already fired the first trackPageView
    }
    matomoTrackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);
}
